<template>
    <label class="fi fv pr ath-form-select" :for="uid">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-select-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <div class="pr ath-form-select-content">
            <select class="pr c-p dw-10"
                ref="input"
                @change="update"
                :name="uid"
                :id="uid"
                :disabled="disabled">
                <slot name="options"></slot>
            </select>
        </div>
    </label>
</template>

<script>
import { guuid, join } from '@aretheyhappy/utils';

export default {
    name: 'mead-form-select',
    props: {
        /**
         * Label i18n key rendered above the select. Only shown when truthy.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc : {type: Boolean, default: () => true},
        /**
         * Currently selected value of the native `<select>` (v-model). The actual `<option>` elements
         * are provided via the `options` slot.
         */
        value       : {type: [String, Object, Boolean], default: () => ''},
        /**
         * Disables the select.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional  : {type: Boolean, default: () => false},
    },
    data () {
        return {uid: guuid()};
    },
    methods: {
        update: function () {
            this.$emit('input', this.$refs.input.value);
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

select {
    font-family: $ff-b;
    font-weight: 400;
    border: 0;
    border-radius: $br-m;
    padding: 0 4.5rem 0 1rem;
    line-height: $lh-xs;
    font-size: $fs-s;
    background-color: var(--c-brand-light-bg);
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);
    appearance: none;
    z-index: 0;
    height: 100%;

    &[disabled="disabled"] {
        cursor: not-allowed;
        pointer-events: none;
        opacity: .65;
    }

    &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 #000;
    }

    &:hover,
    &:active,
    &:hover:focus,
    &:active:focus {
        outline: none;
        box-shadow: inset 0 0 4rem 4rem var(--c-brand-default-bg);
        background-color: var(--c-brand-default-bg);
    }
}

.ath-form-select {
    &.el--invalid {
        select:not(:active),
        select:not(:focus), {
            outline: 1px solid var(--c-brand-danger-bg);
        }
    }
}

.ath-form-select-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
    order: 0;
}

.ath-form-select-content {
    height: 4rem;

    &:after {
        content: '';
        display: inline-block;
        width: 1rem;
        height: 1rem;
        pointer-events: none;
        position: absolute;
        bottom: 1.8rem;
        right: 1.8rem;
        border-left: 1px solid var(--c-brand-light-fg);
        border-top: 1px solid var(--c-brand-light-fg);
        transform: rotate(-135deg);
        z-index: 1;
    }
}

//  SIZES

.ath-form-select.el--s-s .ath-form-select-content {
    height: 3rem;

    &:after {
        bottom: 1.2rem;
        right: 1.2rem;
    }
}

.ath-form-select.el--s-m .ath-form-select-content {
    height: 4rem;

    &:after {
        bottom: 1.8rem;
        right: 1.8rem;
    }
}
</style>
